import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'wouter';
import type { Lang } from '@polyglot/engine';
import { BackLink, Spinner } from '../components/ui';
import { startLocalSession } from '../game/localSession';
import { api, ApiError, ensureSession, useMe } from '../lib/api';
import { formatScore } from '../lib/format';
import { isAndroidApp } from '../lib/platform';

interface PlusStatus {
  offered: boolean;
  available: boolean;
  testMode: boolean;
  plus: boolean;
  subscription: {
    status: string;
    currentPeriodEnd: string | null;
    cancelAtPeriodEnd: boolean;
    refunded: boolean;
  } | null;
  prices: { monthly: string; yearly: string };
  features: string[];
}

export function Plus() {
  const { t, i18n } = useTranslation();
  const [, navigate] = useLocation();
  const { user } = useMe();
  const [status, setStatus] = useState<PlusStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const returned = new URLSearchParams(location.search).get('checkout');

  useEffect(() => {
    let tries = 0;
    let timer: number | undefined;
    const poll = async () => {
      await ensureSession();
      const s = await api<PlusStatus>('GET', '/api/plus');
      setStatus(s);
      // After the hosted checkout, the entitlement arrives by webhook: poll a little, never assume.
      if (returned === 'returned' && !s.plus && tries++ < 10)
        timer = window.setTimeout(() => void poll(), 3000);
    };
    void poll();
    return () => window.clearTimeout(timer);
  }, [returned]);

  if (!status) return <Spinner label={t('common.loading')} />;
  // Inside the Google Play app, never lead to the web checkout (Play Payments policy).
  if (!status.offered || isAndroidApp()) {
    return (
      <main className="page">
        <BackLink to="/me" />
        <h1>Plus</h1>
        <p className="message info">{t('plus.notOffered')}</p>
      </main>
    );
  }

  const checkout = async (plan: 'monthly' | 'yearly') => {
    setError(null);
    try {
      const r = await api<{ url: string }>('POST', '/api/plus/checkout', { plan });
      location.href = r.url;
    } catch (e) {
      setError(
        e instanceof ApiError && e.code === 'payments_unavailable'
          ? t('plus.unavailable')
          : t('errors.generic'),
      );
    }
  };

  return (
    <main className="page">
      <BackLink to="/me" />
      <h1>Plus</h1>
      {returned === 'returned' && !status.plus && (
        <p className="message info">{t('plus.confirming')}</p>
      )}
      {returned === 'cancelled' && <p className="message info">{t('plus.cancelled')}</p>}
      <section className="card">
        <p>{t('plus.intro')}</p>
        <ul>
          {status.features.map((f) => (
            <li key={f}>{t(`plus.features.${f}`)}</li>
          ))}
        </ul>
        <p className="muted small">{t('plus.fairness')}</p>
      </section>
      {status.plus ? (
        <section className="card">
          <p>
            <strong>{t('plus.active')}</strong>
            {status.subscription?.currentPeriodEnd &&
              ` — ${t('plus.until', { date: new Date(status.subscription.currentPeriodEnd).toLocaleDateString(i18n.language) })}`}
          </p>
          <button
            className="btn ghost"
            onClick={async () => {
              const r = await api<{ url: string }>('POST', '/api/plus/portal', {});
              location.href = r.url;
            }}
          >
            {t('plus.manage')}
          </button>
          <PlusTools
            onTrain={async (lang, seed) => {
              await startLocalSession({ mode: 'relax', languages: [lang], bot: null }, seed);
              navigate('/play');
            }}
          />
          <Archive />
        </section>
      ) : !status.available ? (
        <p className="message info">{t('plus.unavailable')}</p>
      ) : user?.kind !== 'registered' ? (
        <Link className="btn primary big" href="/account">
          {t('online.saveAccount')}
        </Link>
      ) : (
        <div className="results-actions">
          <button className="btn primary big" onClick={() => void checkout('monthly')}>
            {t('plus.monthly', { price: status.prices.monthly })}
          </button>
          <button className="btn ghost" onClick={() => void checkout('yearly')}>
            {t('plus.yearly', { price: status.prices.yearly })}
          </button>
          {status.testMode && <p className="muted small">{t('plus.testMode')}</p>}
        </div>
      )}
      {error && <p className="message error">{error}</p>}
      <p className="muted small">{t('plus.pricesNote')}</p>
    </main>
  );
}

function PlusTools({ onTrain }: { onTrain: (lang: Lang, seed: string) => Promise<void> }) {
  const { t } = useTranslation();
  const [stats, setStats] = useState<{
    perLanguage: { lang: string; words: number; avg_len: number; longest: number }[];
    perMode: {
      mode: string;
      rounds: number;
      avg_score: number;
      contract_rate: number;
      sec_per_word: number | null;
    }[];
  } | null>(null);
  const [plan, setPlan] = useState<{ focusLanguage: Lang; seed: string } | null>(null);
  useEffect(() => {
    api<typeof stats>('GET', '/api/me/stats/advanced')
      .then(setStats)
      .catch(() => setStats(null));
    api<typeof plan>('GET', '/api/training/plan')
      .then(setPlan)
      .catch(() => setPlan(null));
  }, []);
  return (
    <>
      <h2>{t('plus.stats')}</h2>
      {stats && (
        <table className="data">
          <thead>
            <tr>
              <th>{t('plus.mode')}</th>
              <th className="num">{t('plus.rounds')}</th>
              <th className="num">{t('plus.avgScore')}</th>
              <th className="num">{t('plus.contractRate')}</th>
              <th className="num">{t('plus.secPerWord')}</th>
            </tr>
          </thead>
          <tbody>
            {stats.perMode.map((m) => (
              <tr key={m.mode}>
                <td>{t(`modes.${m.mode}.name`)}</td>
                <td className="num">{m.rounds}</td>
                <td className="num">{m.avg_score}</td>
                <td className="num">{m.contract_rate} %</td>
                <td className="num">{m.sec_per_word ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {stats && (
        <p className="muted small">
          {stats.perLanguage
            .map((l) => `${l.lang.toUpperCase()}: ${l.words} · ⌀ ${l.avg_len} · max ${l.longest}`)
            .join(' — ')}
        </p>
      )}
      {plan && (
        <button className="btn primary" onClick={() => void onTrain(plan.focusLanguage, plan.seed)}>
          {t('plus.train', { lang: plan.focusLanguage.toUpperCase() })}
        </button>
      )}
    </>
  );
}

interface ArchivedMatch {
  id: string;
  mode: string;
  finished_at: string;
  seats: {
    seat: number;
    me: boolean;
    name: string | null;
    bot: boolean;
    languages: string[] | null;
    words: number;
    finalCents: number | null;
  }[];
}

/** Plus: full archive of finished matches (keyset pagination from the server). */
function Archive() {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState<ArchivedMatch[] | null>(null);
  const [next, setNext] = useState<string | null>(null);
  const load = async (cursor: string | null) => {
    const q = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
    const r = await api<{ matches: ArchivedMatch[]; next: string | null }>(
      'GET',
      `/api/me/archive${q}`,
    );
    setItems((prev) => [...(cursor ? (prev ?? []) : []), ...r.matches]);
    setNext(r.next);
  };
  useEffect(() => {
    load(null).catch(() => setItems([]));
  }, []);
  return (
    <>
      <h2>{t('plus.archive')}</h2>
      {items && items.length === 0 && <p className="muted">{t('plus.archiveEmpty')}</p>}
      {items?.map((m) => (
        <Link key={m.id} href={`/match/${m.id}`} className="row round-row">
          <span>
            {t(`modes.${m.mode}.name`)} ·{' '}
            {new Date(m.finished_at).toLocaleDateString(i18n.language)}
          </span>
          <span className="muted small">
            {m.seats
              .map(
                (s) =>
                  `${s.me ? '★ ' : ''}${s.bot ? `${t('plus.archiveBot')} ` : ''}${s.name ?? '—'} ${
                    s.finalCents === null ? '—' : formatScore(s.finalCents, i18n.language)
                  }`,
              )
              .join(' · ')}
          </span>
        </Link>
      ))}
      {next && (
        <button className="btn ghost small" onClick={() => void load(next)}>
          {t('plus.archiveMore')}
        </button>
      )}
    </>
  );
}
